import { Search, ChevronLeft } from 'lucide-react';
import { STATUS_CONFIG } from '../../lib/formatters';

/* ---- Page Header ---- */
export const PageHeader = ({ title, subtitle, actions, children }) => (
  <div className="mcl-page-header" data-testid="page-header">
    <div className="mcl-page-header-row">
      <div>
        <h1 className="mcl-page-title">{title}</h1>
        {subtitle && <p className="mcl-page-subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>{actions}</div>}
    </div>
    {children}
  </div>
);

/* ---- Section Card ---- */
export const SectionCard = ({ title, actions, flush, children, className = '' }) => (
  <div className={`mcl-card ${className}`}>
    {title && (
      <div className="mcl-card-header">
        <h3 className="mcl-card-header-title">{title}</h3>
        {actions && <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>{actions}</div>}
      </div>
    )}
    <div className={flush ? 'mcl-card-body-flush' : 'mcl-card-body'}>
      {children}
    </div>
  </div>
);

/* ---- Summary Stat Card ---- */
export const SummaryStatCard = ({ icon: Icon, label, value, variant = 'primary' }) => (
  <div className="mcl-stat-card" data-testid={`stat-${label?.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className={`mcl-stat-icon ${variant}`}>
      <Icon size={22} />
    </div>
    <div className="mcl-stat-content">
      <div className="mcl-stat-label">{label}</div>
      <div className="mcl-stat-value">{value}</div>
    </div>
  </div>
);

/* ---- Status Badge ---- */
export const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || {
    label: status
      ? String(status).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
      : 'Unknown',
    variant: 'secondary',
  };
  return (
    <span className={`mcl-badge mcl-badge-${config.variant}`} data-testid={`status-badge-${status || 'unknown'}`}>
      {config.label}
    </span>
  );
};

/* ---- Filter Toolbar ---- */
export const FilterToolbar = ({ searchValue, onSearchChange, searchPlaceholder = 'Search...', children }) => (
  <div className="mcl-filter-bar" style={{ marginBottom: '1.25rem' }}>
    <div className="mcl-search-input">
      <Search size={16} className="search-icon" />
      <input
        type="text"
        placeholder={searchPlaceholder}
        value={searchValue}
        onChange={(e) => onSearchChange(e.target.value)}
        data-testid="search-input"
      />
    </div>
    {children}
  </div>
);

/* ---- Empty State ---- */
export const EmptyState = ({ icon: Icon, title, text, actions }) => (
  <div className="mcl-empty-state" data-testid="empty-state">
    {Icon && (
      <div className="mcl-empty-icon">
        <Icon size={26} />
      </div>
    )}
    <div className="mcl-empty-title">{title}</div>
    {text && <p className="mcl-empty-text">{text}</p>}
    {actions && <div>{actions}</div>}
  </div>
);

/* ---- Back Link ---- */
export const BackLink = ({ onClick, label = 'Back' }) => (
  <button className="mcl-back-link" onClick={onClick} data-testid="back-link">
    <ChevronLeft size={18} />
    {label}
  </button>
);

/* ---- Info Row (for detail pages) ---- */
export const InfoRow = ({ label, value }) => (
  <div className="mcl-info-row">
    <span className="mcl-info-label">{label}</span>
    <span className="mcl-info-value">{value || <span style={{ color: 'var(--mcl-text-light)', fontStyle: 'italic', fontWeight: 400 }}>Not provided</span>}</span>
  </div>
);

/* ---- Loading Spinner ---- */
export const LoadingSpinner = () => (
  <div className="mcl-loading" data-testid="loading-spinner">
    <div className="mcl-spinner" />
  </div>
);

/* ---- Form Section (inside cards) ---- */
export const FormSection = ({ title, children }) => (
  <div className="mcl-card" style={{ marginBottom: '1.25rem' }}>
    {title && (
      <div className="mcl-card-header">
        <h3 className="mcl-card-header-title">{title}</h3>
      </div>
    )}
    <div className="mcl-card-body">
      {children}
    </div>
  </div>
);

/* ---- Editable Form Section ---- */
// `isSaving` defaults to false so existing callers are unaffected. While it is
// true both buttons are disabled and Save reads "Saving..." — the same pattern
// ClientDashboard already uses for its own save buttons. Cancel is disabled too:
// dismissing the editor mid-request would leave the section closed while a PUT
// is still in flight.
export const EditableFormSection = ({ title, sectionKey, isEditing, onEditClick, onSaveClick, onCancelClick, canEdit = true, isSaving = false, children }) => (
  <div className="mcl-card" style={{ marginBottom: '1.25rem' }}>
    <div className="mcl-card-header">
      <h3 className="mcl-card-header-title">{title}</h3>
      {canEdit && (
        <div className="d-flex" style={{ gap: '0.5rem' }}>
          {isEditing ? (
            <>
              <button className="mcl-btn mcl-btn-sm mcl-btn-success" onClick={onSaveClick} disabled={isSaving} data-testid={`save-section-${sectionKey}`}>
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button className="mcl-btn mcl-btn-sm mcl-btn-outline" onClick={onCancelClick} disabled={isSaving} data-testid={`cancel-section-${sectionKey}`}>
                Cancel
              </button>
            </>
          ) : (
            <button className="mcl-btn mcl-btn-sm mcl-btn-outline-primary" onClick={onEditClick} data-testid={`edit-section-${sectionKey}`}>
              Edit
            </button>
          )}
        </div>
      )}
    </div>
    <div className="mcl-card-body">
      {children}
    </div>
  </div>
);

/* ---- Read Only Value ---- */
export const ReadOnlyValue = ({ value, placeholder = 'Not provided' }) => (
  <div className={`mcl-readonly-value ${!value ? 'empty' : ''}`}>
    {value || placeholder}
  </div>
);

/* ---- Radio Group ---- */
export const RadioGroup = ({ name, options, value, onChange, required = false, disabled = false }) => (
  <div className="mcl-radio-group">
    {options.map((option) => (
      <label key={option.value} className="mcl-radio-label" style={disabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}}>
        <input
          type="radio"
          name={name}
          value={option.value}
          checked={value === option.value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          disabled={disabled}
        />
        <span>{option.label}</span>
      </label>
    ))}
  </div>
);

/* ---- Alert ---- */
export const Alert = ({ variant = 'info', children }) => (
  <div className={`mcl-alert mcl-alert-${variant}`}>
    {children}
  </div>
);
